import { Button, Heading, Separator, Slider, Text } from "@overmap-ai/blocks"
import { selectAssetsOfProject, selectAssetTypesOfOrganization } from "@overmap-ai/core"
import { memo, useCallback, useMemo } from "react"

import { AssetMapColorMode } from "../../../enums/assets"
import { assetMapColorModeData } from "../../../mappings/assets"
import {
	setAssetMapColorMode,
	setAssetGeometryFillOpacity,
	setAssetGeometryStrokeOpacity,
	useAppDispatch,
	useAppSelector,
	useShallowEqaulSelector,
} from "../../../store"
import {
	selectActiveOrganizationId,
	selectActiveProjectId,
	selectAssetMapColorMode,
	selectAssetGeometryFillOpacity,
	selectAssetGeometryStrokeOpacity,
} from "../../../store/selectors"
import { AssetColorModeDropdownSelect } from "../../DropdownSelects/AssetColorModeDropdownSelect"
import { AssetTypeVisibilityItem } from "../VisibilityItems/AssetTypeVisibilityItem"

export const AssetTypeVisibilityTab = memo(() => {
	const activeOrganizationId = useAppSelector(selectActiveOrganizationId)!
	const activeProjectId = useAppSelector(selectActiveProjectId)!

	const allAssetTypes = useShallowEqaulSelector(selectAssetTypesOfOrganization(activeOrganizationId))
	const assets = useAppSelector(selectAssetsOfProject(activeProjectId))
	const assetMapColorMode = useAppSelector(selectAssetMapColorMode)
	const assetGeometryFillOpacity = useAppSelector(selectAssetGeometryFillOpacity)
	const assetGeometryStrokeOpacity = useAppSelector(selectAssetGeometryStrokeOpacity)
	const dispatch = useAppDispatch()

	// Filter asset types to only show those that have assets
	const assetTypes = useMemo(() => {
		const assetTypeIdsWithAssets = new Set(assets.map((asset) => asset.asset_type))
		return allAssetTypes.filter((assetType) => assetTypeIdsWithAssets.has(assetType.uuid))
	}, [allAssetTypes, assets])

	const handleAssetMapColorMode = useCallback(
		(mode: AssetMapColorMode) => {
			dispatch(setAssetMapColorMode(mode))
		},
		[dispatch],
	)

	const handleAssetGeometryFillOpacityChange = useCallback(
		([value]: number[]) => {
			dispatch(setAssetGeometryFillOpacity(value))
		},
		[dispatch],
	)

	const handleAssetGeometryStrokeOpacityChange = useCallback(
		([value]: number[]) => {
			dispatch(setAssetGeometryStrokeOpacity(value))
		},
		[dispatch],
	)

	return (
		<div className="flex flex-col gap-4">
			<section className="flex w-full flex-col gap-2">
				<div className="flex w-full items-center justify-between">
					<Heading>Assets</Heading>
				</div>

				{assetTypes.length > 0 ? (
					<ul className="flex flex-col gap-2">
						{assetTypes.map((assetType) => {
							return <AssetTypeVisibilityItem key={assetType.uuid} assetType={assetType} />
						})}
					</ul>
				) : (
					<div className="flex w-full">
						<Text accentColor="base" weight="regular" size="sm">
							No assets.
						</Text>
					</div>
				)}
			</section>

			<Separator size="full" />

			<div className="flex w-full flex-col gap-2">
				<Text>Color</Text>
				<div className="flex w-full flex-col justify-between gap-2">
					<Text size="sm" accentColor="base">
						Configure the color of asset geometries visible on the map. Available options in the dropdown
						below.
					</Text>

					<AssetColorModeDropdownSelect
						required
						align="start"
						size="sm"
						value={assetMapColorMode}
						onValueChange={handleAssetMapColorMode}
					>
						<Button variant="soft" accentColor="base" size="sm" className="w-max">
							{assetMapColorModeData[assetMapColorMode].label}
						</Button>
					</AssetColorModeDropdownSelect>
				</div>
			</div>

			<Separator size="full" />

			<div className="flex w-full flex-col justify-between gap-2">
				<Text>Fill opacity</Text>

				<Text size="sm" accentColor="base">
					Configure the stroke opacity of asset geomtries visible on the map.
				</Text>

				<Slider
					radius="full"
					size="sm"
					min={0}
					max={1}
					step={0.1}
					defaultValue={[assetGeometryFillOpacity]}
					onValueCommit={handleAssetGeometryFillOpacityChange}
				/>
			</div>

			<Separator size="full" />

			<div className="flex w-full flex-col justify-between gap-2">
				<Text>Stroke opacity</Text>

				<Text size="sm" accentColor="base">
					Configure the stroke opacity of asset geomtries visible on the map.
				</Text>

				<Slider
					radius="full"
					size="sm"
					min={0}
					max={1}
					step={0.1}
					defaultValue={[assetGeometryStrokeOpacity]}
					onValueCommit={handleAssetGeometryStrokeOpacityChange}
				/>
			</div>
		</div>
	)
})
AssetTypeVisibilityTab.displayName = "AssetTypeVisibilityTab"
